import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { diffLines } from "diff";

const DEFAULT_GRAPH_STATE_OPTIONS = [
  { value: "Draft", label: "Draft" },
  { value: "Published", label: "Published" },
];

const ALL_GRAPH_STATE_OPTIONS = DEFAULT_GRAPH_STATE_OPTIONS.concat([
  { value: "All", label: "All" },
]);

const FRESHNESS_META = {
  Current: {
    label: "Current",
    backgroundColor: "#edf7ed",
    color: "#1b5e20",
  },
  Stale: {
    label: "Stale",
    backgroundColor: "#fff8e1",
    color: "#8d6e00",
  },
  Missing: {
    label: "Missing",
    backgroundColor: "#fdecea",
    color: "#b71c1c",
  },
  NotApplicable: {
    label: "N/A",
    backgroundColor: "#eceff3",
    color: "#42526e",
  },
};

const useStyles = makeStyles((theme) => ({
  panel: {
    marginTop: theme.spacing(3),
  },
  subCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#ffffff",
    height: "100%",
  },
  title: {
    fontWeight: 600,
    color: "#171f32",
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: "#5f6676",
    marginBottom: theme.spacing(1.5),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  localeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(1.5),
  },
  localeCard: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#fbfaf7",
  },
  smallLabel: {
    fontSize: 12,
    color: "#6f7687",
    fontFamily: '"Roboto Mono", "Courier New", monospace',
  },
  scheduleField: {
    marginBottom: theme.spacing(2),
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  diffShell: {
    borderRadius: 12,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    overflow: "hidden",
    marginTop: theme.spacing(1.5),
  },
  diffHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    backgroundColor: "#171f32",
    color: "#f8f6ef",
    fontWeight: 600,
  },
  diffHeaderCell: {
    padding: theme.spacing(1.25, 1.5),
    borderRight: "1px solid rgba(248, 246, 239, 0.14)",
  },
  diffBody: {
    maxHeight: 320,
    overflowY: "auto",
    backgroundColor: "#ffffff",
  },
  diffRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    borderTop: "1px solid rgba(23, 31, 50, 0.06)",
  },
  diffCell: {
    padding: theme.spacing(1, 1.5),
    borderRight: "1px solid rgba(23, 31, 50, 0.06)",
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    minHeight: 42,
  },
  diffAdded: {
    backgroundColor: "#edf7ed",
  },
  diffRemoved: {
    backgroundColor: "#fdecea",
  },
  diffSame: {
    backgroundColor: "#ffffff",
  },
  diffEmpty: {
    backgroundColor: "#f8f9fb",
    color: "#a0a7b4",
  },
  metadataRow: {
    display: "grid",
    gridTemplateColumns: "180px 1fr 1fr",
    gap: theme.spacing(1),
    alignItems: "start",
    padding: theme.spacing(1, 0),
    borderTop: "1px solid rgba(23, 31, 50, 0.06)",
  },
  metadataLabel: {
    fontWeight: 600,
    color: "#171f32",
  },
  metadataValue: {
    minHeight: 40,
    borderRadius: 10,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#fbfaf7",
    padding: theme.spacing(1),
  },
  selectRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    marginBottom: theme.spacing(1.5),
  },
  warningCard: {
    padding: theme.spacing(1.25),
    borderRadius: 12,
    backgroundColor: "#fff8e1",
    border: "1px solid rgba(141, 110, 0, 0.18)",
    color: "#8d6e00",
  },
  graphFrame: {
    width: "100%",
    minHeight: 260,
    borderRadius: 12,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    background:
      "linear-gradient(180deg, rgba(248,250,255,0.98), rgba(250,247,240,0.98))",
    marginTop: theme.spacing(1.5),
  },
  graphMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  graphLegend: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  graphList: {
    marginTop: theme.spacing(1.5),
    borderRadius: 12,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#fbfaf7",
    maxHeight: 280,
    overflowY: "auto",
  },
  graphListItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    borderTop: "1px solid rgba(23, 31, 50, 0.06)",
  },
  graphListLabel: {
    fontWeight: 600,
    color: "#171f32",
  },
}));

const formatDate = (value) => {
  if (!value) {
    return "Not available";
  }

  return new Date(value).toLocaleString();
};

const formatDateTimeInput = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getFreshnessMeta = (status) =>
  FRESHNESS_META[status] || FRESHNESS_META.NotApplicable;

const normalizeDiffLines = (value) => {
  const lines = (value || "").split("\n");
  if (lines.length === 0) {
    return [""];
  }

  if (lines[lines.length - 1] === "") {
    lines.pop();
  }

  return lines.length === 0 ? [""] : lines;
};

const buildSplitDiffRows = (leftValue, rightValue) => {
  const parts = diffLines(leftValue || "", rightValue || "");

  return parts.reduce((rows, part) => {
    const lines = normalizeDiffLines(part.value);

    if (part.added) {
      return rows.concat(
        lines.map((line) => ({
          leftText: "",
          leftState: "empty",
          rightText: line,
          rightState: "added",
        }))
      );
    }

    if (part.removed) {
      return rows.concat(
        lines.map((line) => ({
          leftText: line,
          leftState: "removed",
          rightText: "",
          rightState: "empty",
        }))
      );
    }

    return rows.concat(
      lines.map((line) => ({
        leftText: line,
        leftState: "same",
        rightText: line,
        rightState: "same",
      }))
    );
  }, []);
};

const getCellClassName = (classes, state) => {
  if (state === "added") {
    return `${classes.diffCell} ${classes.diffAdded}`;
  }

  if (state === "removed") {
    return `${classes.diffCell} ${classes.diffRemoved}`;
  }

  if (state === "empty") {
    return `${classes.diffCell} ${classes.diffEmpty}`;
  }

  return `${classes.diffCell} ${classes.diffSame}`;
};

const normalizeMetadataValue = (value) => {
  if (Array.isArray(value)) {
    return value.length === 0 ? "None" : value.join(", ");
  }

  if (value === null || value === undefined || value === "") {
    return "Not set";
  }

  return String(value);
};

const buildGraphLayout = (graph) => {
  const nodes = graph && graph.nodes ? graph.nodes : [];
  const root = nodes.find((node) => node.nodeId === (graph && graph.rootNodeId));
  if (!root) {
    return { width: 840, height: 260, positions: {} };
  }

  const edges = graph.edges || [];
  const rootId = graph.rootNodeId;
  const positions = {};
  const inbound = [];
  const outboundTemplates = [];
  const imageNodes = [];
  const otherNodes = [];

  nodes
    .filter((node) => node.nodeId !== rootId)
    .forEach((node) => {
      const isInbound = edges.some(
        (edge) => edge.sourceNodeId === node.nodeId && edge.targetNodeId === rootId
      );
      const isImage = node.nodeType === "ImageAsset";
      const isTemplate = node.nodeType === "Template";

      if (isInbound) {
        inbound.push(node);
      } else if (isImage) {
        imageNodes.push(node);
      } else if (isTemplate) {
        outboundTemplates.push(node);
      } else {
        otherNodes.push(node);
      }
    });

  const stackNodes = (items, x, startY) =>
    items.forEach((node, index) => {
      positions[node.nodeId] = {
        x,
        y: startY + index * 92,
      };
    });

  positions[rootId] = {
    x: 320,
    y: 120,
  };
  stackNodes(inbound, 40, 40);
  stackNodes(outboundTemplates, 570, 40);
  stackNodes(imageNodes, 570, 220 + outboundTemplates.length * 8);
  stackNodes(otherNodes, 320, 240);

  const maxCount = Math.max(
    1,
    inbound.length,
    outboundTemplates.length + imageNodes.length,
    otherNodes.length
  );

  return {
    width: 840,
    height: Math.max(280, 140 + maxCount * 92),
    positions,
  };
};

const getNodeFill = (node, isRoot) => {
  if (isRoot) {
    return "#171f32";
  }

  if (node.isMissing) {
    return "#fdecea";
  }

  if (node.isArchived) {
    return "#fff8e1";
  }

  if (node.nodeType === "ImageAsset") {
    return "#edf7ed";
  }

  return "#eef2ff";
};

const getNodeStroke = (node, isRoot) => {
  if (isRoot) {
    return "#171f32";
  }

  if (node.isMissing) {
    return "#b71c1c";
  }

  if (node.isArchived) {
    return "#8d6e00";
  }

  if (node.nodeType === "ImageAsset") {
    return "#1b5e20";
  }

  return "#34415e";
};

export const createHistoryCompareOptions = (history) => {
  const options = [
    { value: "published", label: "Published" },
  ];

  (history || []).forEach((revision) => {
    options.push({
      value: `revision:${revision.notificationTemplateRevisionId}`,
      label: `${revision.action} - ${formatDate(
        revision.audit && revision.audit.updatedDateUtc
      )}`,
    });
  });

  return options;
};

export const getCompareTargetLabel = (compareTarget, options) => {
  const match = (options || []).find((option) => option.value === compareTarget);
  return match ? match.label : "Published";
};

export const NotificationLocalePanel = ({
  title,
  freshness,
  localeStatuses,
}) => {
  const classes = useStyles();
  const draftFreshness = getFreshnessMeta(
    freshness && freshness.draftStatus
  );
  const publishedFreshness = getFreshnessMeta(
    freshness && freshness.publishedStatus
  );
  const draftStyle = {
    backgroundColor: draftFreshness.backgroundColor,
    color: draftFreshness.color,
  };
  const publishedStyle = {
    backgroundColor: publishedFreshness.backgroundColor,
    color: publishedFreshness.color,
  };

  return (
    <div className={classes.panel}>
      <Typography variant="subtitle1" className={classes.title}>
        {title || "Locales"}
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Track freshness against the `en-US` source and see which locales are current,
        stale, or still missing before you publish.
      </Typography>

      <div className={classes.chipRow}>
        <Chip
          label={`Draft: ${draftFreshness.label}`}
          style={draftStyle}
        />
        <Chip
          label={`Published: ${publishedFreshness.label}`}
          style={publishedStyle}
        />
        <Chip
          label={`Last synced: ${formatDate(
            freshness && freshness.lastSyncedAtUtc
          )}`}
        />
        <Chip
          label={`Sync mode: ${
            (freshness && freshness.syncMode) || "Not recorded"
          }`}
        />
      </div>

      <div className={classes.localeGrid}>
        {(localeStatuses || []).map((localeStatus) => {
          const meta = getFreshnessMeta(
            localeStatus &&
              localeStatus.freshness &&
              localeStatus.freshness.draftStatus
          );
          const metaStyle = {
            backgroundColor: meta.backgroundColor,
            color: meta.color,
          };

          return (
            <div key={localeStatus.locale} className={classes.localeCard}>
              <Typography variant="subtitle2" className={classes.graphListLabel}>
                {localeStatus.displayName}
              </Typography>
              <Typography className={classes.smallLabel}>
                {localeStatus.locale}
              </Typography>
              <div className={classes.chipRow}>
                {localeStatus.isDefault ? (
                <Chip size="small" label="Source locale" />
                ) : null}
                <Chip
                  size="small"
                  label={localeStatus.exists ? "Managed" : "Missing"}
                  style={metaStyle}
                />
              </div>
              <Typography variant="body2" color="textSecondary">
                Published source update:{" "}
                {formatDate(
                  localeStatus.freshness &&
                    localeStatus.freshness.sourcePublishedUpdatedDateUtc
                )}
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const NotificationSchedulePanel = ({
  title,
  schedule,
  busy,
  onSave,
  onCancel,
}) => {
  const classes = useStyles();
  const testIdBase = (title || "schedule")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
  const [runAt, setRunAt] = useState("");

  useEffect(() => {
    if (schedule && schedule.runAtUtc) {
      setRunAt(formatDateTimeInput(schedule.runAtUtc));
      return;
    }

    const nextHour = new Date();
    nextHour.setHours(nextHour.getHours() + 1);
    nextHour.setMinutes(0, 0, 0);
    setRunAt(formatDateTimeInput(nextHour.toISOString()));
  }, [schedule]);

  return (
    <div className={classes.panel}>
      <Typography variant="subtitle1" className={classes.title}>
        {title || "Schedule"}
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Freeze the current draft into a scheduled release. Replacing the schedule
        snapshots the latest draft again.
      </Typography>
      <Paper className={classes.subCard}>
        <TextField
          fullWidth
          type="datetime-local"
          variant="outlined"
          label="Run at"
          value={runAt}
          onChange={(event) => setRunAt(event.target.value)}
          className={classes.scheduleField}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": `${testIdBase}-input` }}
        />
        <div className={classes.chipRow}>
          <Chip
            label={`State: ${(schedule && schedule.state) || "Not scheduled"}`}
          />
          <Chip
            label={`Requested by: ${
              (schedule && schedule.requestedBy) || "Not scheduled"
            }`}
          />
          <Chip
            label={`Requested at: ${formatDate(
              schedule && schedule.requestedAtUtc
            )}`}
          />
        </div>
        {schedule && schedule.snapshotAudit ? (
          <div className={classes.chipRow}>
            <Chip
              label={`Draft snapshot: ${formatDate(
                schedule.snapshotAudit.draftUpdatedDateUtc
              )}`}
            />
            <Chip
              label={`Published snapshot: ${formatDate(
                schedule.snapshotAudit.publishedUpdatedDateUtc
              )}`}
            />
          </div>
        ) : null}
        {schedule && schedule.failureMessage ? (
          <div className={classes.warningCard}>{schedule.failureMessage}</div>
        ) : null}
        <div className={classes.actionRow}>
          <Button
            color="primary"
            variant="contained"
            onClick={() => onSave(runAt)}
            disabled={busy || !runAt}
            data-testid={`${testIdBase}-save`}
          >
            {schedule ? "Replace Schedule" : "Schedule Publish"}
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={busy || !schedule}
            data-testid={`${testIdBase}-cancel`}
          >
            Cancel Schedule
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export const NotificationChangesPanel = ({
  title,
  compareSource,
  compareTarget,
  compareTargetOptions,
  onCompareSourceChange,
  onCompareTargetChange,
  leftLabel,
  rightLabel,
  fields,
  metadata,
}) => {
  const classes = useStyles();
  const testIdBase = (title || "changes")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");

  return (
    <div className={classes.panel}>
      <Typography variant="subtitle1" className={classes.title}>
        {title || "Changes"}
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Diff the live states or load any revision from history to inspect text,
        HTML, tokens, images, and base-template changes side by side.
      </Typography>
      <div className={classes.selectRow}>
        <FormControl variant="outlined">
          <InputLabel id={`${title}-source-label`}>Compare</InputLabel>
          <Select
            labelId={`${title}-source-label`}
            value={compareSource}
            onChange={(event) => onCompareSourceChange(event.target.value)}
            label="Compare"
            inputProps={{ "data-testid": `${testIdBase}-source-select` }}
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined">
          <InputLabel id={`${title}-target-label`}>Against</InputLabel>
          <Select
            labelId={`${title}-target-label`}
            value={compareTarget}
            onChange={(event) => onCompareTargetChange(event.target.value)}
            label="Against"
            inputProps={{ "data-testid": `${testIdBase}-target-select` }}
          >
            {(compareTargetOptions || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {(fields || []).map((field) => {
        const rows = buildSplitDiffRows(field.leftValue, field.rightValue);

        return (
          <div key={field.label}>
            <Typography variant="subtitle2" className={classes.title}>
              {field.label}
            </Typography>
            <div className={classes.diffShell}>
              <div className={classes.diffHeader}>
                <div className={classes.diffHeaderCell}>{leftLabel}</div>
                <div className={classes.diffHeaderCell}>{rightLabel}</div>
              </div>
              <div className={classes.diffBody}>
                {rows.map((row, index) => (
                  <div key={`${field.label}-${index}`} className={classes.diffRow}>
                    <div className={getCellClassName(classes, row.leftState)}>
                      {row.leftText || "\u00a0"}
                    </div>
                    <div className={getCellClassName(classes, row.rightState)}>
                      {row.rightText || "\u00a0"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {(metadata || []).length > 0 ? (
        <Paper className={classes.subCard}>
          <Typography variant="subtitle2" className={classes.title}>
            Metadata
          </Typography>
          {(metadata || []).map((item) => (
            <div key={item.label} className={classes.metadataRow}>
              <div className={classes.metadataLabel}>{item.label}</div>
              <div className={classes.metadataValue}>
                {normalizeMetadataValue(item.leftValue)}
              </div>
              <div className={classes.metadataValue}>
                {normalizeMetadataValue(item.rightValue)}
              </div>
            </div>
          ))}
        </Paper>
      ) : null}
    </div>
  );
};

export const NotificationDependencyGraphPanel = ({
  title,
  graph,
  loading,
  state,
  stateOptions,
  onStateChange,
  onRefresh,
  onNodeClick,
}) => {
  const classes = useStyles();
  const layout = useMemo(() => buildGraphLayout(graph), [graph]);
  const options = stateOptions || DEFAULT_GRAPH_STATE_OPTIONS;
  const testIdBase = (title || "dependencies")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");

  return (
    <div className={classes.panel}>
      <Typography variant="subtitle1" className={classes.title}>
        {title || "Dependencies"}
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Inspect outbound base and image references, inbound dependents, and any
        missing or archived nodes before you publish.
      </Typography>
      <div className={classes.selectRow}>
        <FormControl variant="outlined">
          <InputLabel id={`${title}-graph-state-label`}>State</InputLabel>
          <Select
            labelId={`${title}-graph-state-label`}
            value={state}
            onChange={(event) => onStateChange(event.target.value)}
            label="State"
            inputProps={{ "data-testid": `${testIdBase}-state-select` }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          color="primary"
          onClick={onRefresh}
          disabled={loading}
          data-testid={`${testIdBase}-load`}
        >
          {loading ? "Loading..." : "Load Dependencies"}
        </Button>
      </div>

      {graph ? (
        <>
          <div className={classes.graphMeta}>
            <Chip label={`Nodes: ${(graph.nodes || []).length}`} />
            <Chip label={`Edges: ${(graph.edges || []).length}`} />
            <Chip label={`Warnings: ${(graph.warnings || []).length}`} />
          </div>
          {(graph.warnings || []).map((warning, index) => (
            <div key={`${warning}-${index}`} className={classes.warningCard}>
              {warning}
            </div>
          ))}
          <svg
            className={classes.graphFrame}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
          >
            {(graph.edges || []).map((edge) => {
              const source = layout.positions[edge.sourceNodeId];
              const target = layout.positions[edge.targetNodeId];
              if (!source || !target) {
                return null;
              }

              return (
                <line
                  key={`${edge.sourceNodeId}-${edge.targetNodeId}-${edge.relationship}`}
                  x1={source.x + 110}
                  y1={source.y + 28}
                  x2={target.x + 110}
                  y2={target.y + 28}
                  stroke="#9aa4b2"
                  strokeWidth="2"
                />
              );
            })}
            {(graph.nodes || []).map((node) => {
              const position = layout.positions[node.nodeId];
              if (!position) {
                return null;
              }

              const isRoot = node.nodeId === graph.rootNodeId;
              return (
                <g
                  key={node.nodeId}
                  style={{ cursor: onNodeClick ? "pointer" : "default" }}
                  onClick={() => onNodeClick && onNodeClick(node)}
                >
                  <rect
                    x={position.x}
                    y={position.y}
                    width="220"
                    height="56"
                    rx="14"
                    fill={getNodeFill(node, isRoot)}
                    stroke={getNodeStroke(node, isRoot)}
                    strokeWidth="2"
                  />
                  <text
                    x={position.x + 14}
                    y={position.y + 24}
                    fill={isRoot ? "#f8f6ef" : "#171f32"}
                    fontSize="13"
                    fontWeight="600"
                  >
                    {node.label}
                  </text>
                  <text
                    x={position.x + 14}
                    y={position.y + 42}
                    fill={isRoot ? "#f8f6ef" : "#5f6676"}
                    fontSize="11"
                  >
                    {node.nodeType}
                    {node.state ? ` - ${node.state}` : ""}
                  </text>
                </g>
              );
            })}
          </svg>

          <div className={classes.graphLegend}>
            <Chip size="small" label="Blue: templates" />
            <Chip size="small" label="Green: images" />
            <Chip size="small" label="Yellow: archived" />
            <Chip size="small" label="Red: missing" />
          </div>

          <List className={classes.graphList}>
            {(graph.nodes || []).map((node) => (
              <ListItem key={node.nodeId} className={classes.graphListItem}>
                <div>
                  <Typography variant="subtitle2" className={classes.graphListLabel}>
                    {node.label}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {node.nodeType}
                    {node.templateKey ? ` - ${node.templateKey}` : ""}
                    {node.brand ? ` - ${node.brand}` : ""}
                    {node.locale ? ` / ${node.locale}` : ""}
                  </Typography>
                </div>
                {onNodeClick ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onNodeClick(node)}
                  >
                    Open
                  </Button>
                ) : null}
              </ListItem>
            ))}
          </List>
        </>
      ) : (
        <Paper className={classes.subCard}>
          <Typography variant="body2" color="textSecondary">
            Load the dependency graph to inspect what this draft will touch.
          </Typography>
        </Paper>
      )}
    </div>
  );
};

export const GRAPH_STATE_OPTIONS = DEFAULT_GRAPH_STATE_OPTIONS;
export const GRAPH_STATE_OPTIONS_WITH_ALL = ALL_GRAPH_STATE_OPTIONS;
